import Label from '@/components/ui/Label';
import { LucideIcon } from 'lucide-react';
import cn from 'classnames';
import { InputHTMLAttributes, ReactNode } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

export interface InputStringProps
  extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  required?: boolean;
  register?: UseFormRegisterReturn;
  error?: string;
  icon?: LucideIcon;
  button?: LucideIcon;
  onButtonClick?: () => void;
}

const InputString = ({
  label,
  required = false,
  className = '',
  type = 'text',
  register,
  error,
  icon: Icon,
  button: ButtonIcon,
  onButtonClick,
  ...props
}: InputStringProps) => {
  const baseInputClasses =
    "block text-sm rounded-md border enabled:hover:border-gray-600 disabled:cursor-not-allowed focus:border-gray-500 focus:outline-none aria-[invalid='true']:border-red-500 aria-[invalid='true']:focus:border-red-500";
  const paddingClasses = Icon ? 'pl-10' : 'px-4';

  const renderErrorMessage = (): ReactNode => {
    if (!error) return null;

    return (
      <p
        id={props.id ? `${props.id}-error` : undefined}
        role="alert"
        className="text-xs pt-1 text-red-500"
      >
        {error}
      </p>
    );
  };

  const handleButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    onButtonClick?.();
  };

  return (
    <>
      {label && (
        <Label htmlFor={props.id} isRequired={required}>
          {label}
        </Label>
      )}
      <div className="relative inline-block w-full">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            <Icon size={16} />
          </div>
        )}
        <input
          type={type}
          className={cn(baseInputClasses, paddingClasses, className)}
          {...register}
          {...props}
          required={!!(required && !register)}
          aria-invalid={!!error}
        />
        {ButtonIcon && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            onClick={handleButtonClick}
            tabIndex={-1}
          >
            <ButtonIcon size={16} />
          </button>
        )}
      </div>
      {renderErrorMessage()}
    </>
  );
};

export default InputString;
